'use client';

import { Component, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // Always log errors for debugging
    console.error('❌ Error caught by ErrorBoundary:', error);
    console.error('Error Info:', errorInfo);
    console.error('Error Stack:', error.stack);
    
    // Log component stack if available
    if (errorInfo?.componentStack) {
      console.error('Component Stack:', errorInfo.componentStack);
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDevelopment = process.env.NODE_ENV === 'development';
      const error = this.state.error;

      return (
        <div className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-900 p-4">
          <div className="text-center max-w-2xl w-full">
            <div className="text-6xl mb-4 flex items-center justify-center">
              <AlertCircle className="w-16 h-16 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              We&apos;re sorry, but something unexpected happened. Please try refreshing the page.
            </p>
            
            {/* Always show error details - helps with debugging */}
            {error && (
              <details className="mt-4 text-left bg-gray-100 dark:bg-dark-800 rounded-lg p-4 mb-6" open>
                <summary className="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Error Details
                </summary>
                <div className="mt-3 space-y-2">
                  <div>
                    <strong className="text-red-600 dark:text-red-400">Error Message:</strong>
                    <p className="text-sm text-gray-800 dark:text-gray-200 font-mono break-words">
                      {error.message || 'Unknown error'}
                    </p>
                  </div>
                  {error.stack && (
                    <div>
                      <strong className="text-red-600 dark:text-red-400">Stack Trace:</strong>
                      <pre className="text-xs text-gray-800 dark:text-gray-200 overflow-auto max-h-64 mt-1 p-2 bg-gray-50 dark:bg-dark-900 rounded">
                        {error.stack}
                      </pre>
                    </div>
                  )}
                  <div className="mt-3 text-xs text-gray-600 dark:text-gray-400">
                    <p><strong>Tip:</strong> Check the browser console (F12) and share the red error message.</p>
                    {error.message?.includes('backend') || error.message?.includes('API') || error.message?.includes('fetch') ? (
                      <p className="mt-2 text-yellow-700 dark:text-yellow-400">
                        <strong>⚠️ Possible Issue:</strong> Backend server may not be running or returned an error.
                      </p>
                    ) : null}
                  </div>
                </div>
              </details>
            )}
            
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: undefined });
                  window.location.reload();
                }}
                className="btn btn-primary"
              >
                Refresh Page
              </button>
              <button
                onClick={() => {
                  window.location.href = '/';
                }}
                className="btn btn-secondary"
              >
                Go to Home
              </button>
              <button
                onClick={() => {
                  // Allow user to clear the error boundary without navigation
                  this.setState({ hasError: false, error: undefined });
                }}
                className="btn btn-outline"
              >
                Close Message
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

